import { FC, useRef, useState } from 'react';
import { useSelector, useDispatch, useParams, useLocation } from 'umi';
import { useIntl, FormattedMessage } from 'react-intl';
import moment from 'moment';
import { useMount } from 'ahooks';
import { message, Button, Row, Col } from 'antd';
import { message as iglooMessage } from 'iglooform';
import { Modal } from 'iglooform';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import classnames from 'classnames';
import numeral from 'numeral';
import ListItemPanel from '@/components/list-item-panel';
import Amount from '@/components/amount';
import ListItem from '@/components/list-item';
import PageFrame from '@/components/page-frame';
import premiumConfig from '@/configs/premium';
import specialIntlMessages from '@/utils/specialIntlMessages';
import { get } from 'lodash';
import useMobile from '@/hooks/useMobile';
import useUpdateRemoteLangMessage from '@/hooks/useUpdateRemoteLangMessage';
import styles from './index.less';

const Payment: FC = () => {
  const { planInfo, planCmsInfo, quotationInfo } = useSelector<
    ReduxState,
    PaymentState
  >((state) => ({
    ...state.payment,
  }));
  const isMobile = useMobile();
  const location: any = useLocation();
  const { formatMessage } = useIntl();
  const [visible, setVisible] = useState(false);
  const copyButton = useRef<any>(null);
  const params = useParams<{ type: TTypes }>();
  const dispatch = useDispatch();

  const productType = params.type;

  const [redirectToUrl, setRedirectToUrl] = useState('');

  let { coverage_type: coverageType } = planCmsInfo;
  coverageType = coverageType ?? productType;

  let {
    productKey,
    planKey,
    applicationId,
    quotationId,
    policyStartDate,
    rePay,
  } = location.query;
  policyStartDate = Number(policyStartDate);

  useMount(() => {
    dispatch({
      type: 'payment/getPlanInfo',
      payload: {
        productType: productType,
        productId: productKey,
        planId: planKey,
      },
    });
    dispatch({
      type: 'payment/getPlanCmsDetail',
      payload: {
        productType: productType,
        productId: productKey,
        planId: planKey,
      },
    });
    dispatch({
      type: 'payment/getQuotation',
      payload: {
        quotationId,
      },
    });

    //event tracking: check out
    window.gtag('event', 'check_out', {
      quote_id: quotationId,
      product_key: productKey,
      plan_key: planKey,
      user_unique_id: JSON.parse(localStorage.getItem('user_info') ?? '{}')?.id,
    });
  });

  useUpdateRemoteLangMessage([
    () =>
      dispatch({
        type: 'payment/getPlanCmsDetail',
        payload: {
          productType: productType,
          productId: productKey,
          planId: planKey,
        },
      }),
  ]);

  const payNow = (emptyUrl?: boolean) =>
    dispatch({
      type: 'payment/payNow',
      payload: {
        productType: productType,
        applicationId,
        params: {
          cancel: false,
          failed_redirect_url: emptyUrl ? '' : window.location.origin,
          succeed_redirect_url: emptyUrl ? '' : window.location.origin,
          pending_redirect_url: emptyUrl ? '' : window.location.origin,
        },
        exceptionCallback: (state: string) => {
          switch (state) {
            case 'succeed':
              message.info({
                className: 'ignite-message-notice',
                content: formatMessage({
                  id: `A payment for this customer's policy has recently been completed.`,
                }),
              });
              //event tracking: pay now
              window.gtag('event', rePay ? 're_payment' : 'payment', {
                product_key: productKey,
                plan_key: planKey,
                quote_id: quotationId,
                user_unique_id: JSON.parse(
                  localStorage.getItem('user_info') ?? '{}',
                )?.id,
                success: true,
              });
              break;
            default:
              //event tracking: pay now
              window.gtag('event', rePay ? 're_payment' : 'payment', {
                product_key: productKey,
                plan_key: planKey,
                quote_id: quotationId,
                user_unique_id: JSON.parse(
                  localStorage.getItem('user_info') ?? '{}',
                )?.id,
                success: false,
              });
              break;
          }
        },
      },
    });

  const handleMakePayment = () => {
    if (+moment() > policyStartDate) {
      Modal.confirm({
        title: formatMessage({ id: 'Start Date Expired' }),
        content: formatMessage({
          id: 'The policy start date is expired, the system will automatically modify your start date to today and the end date will extend respectively.',
        }),
        okText: formatMessage({ id: 'Got it' }),
        cancelText: formatMessage({ id: 'No' }),
        onOk: () => {
          setVisible(true);
        },
      });
      return;
    }
    setVisible(true);
  };

  const renderMotorPlan = () => {
    const benefits = planCmsInfo?.cms?.data?.attributes?.benefits ?? [];
    const includePa = quotationInfo?.fast_quote?.include_pa === 'Yes';
    const insurer = planCmsInfo?.cms?.data?.attributes?.insurer || {};
    const logoUrl =
      insurer?.data?.attributes?.logo?.data?.attributes?.url ||
      planInfo?.details?.logo_url;
    const planName =
      planCmsInfo.cms?.data?.attributes?.planName || planInfo?.name;

    const benefitsFieldList = benefits.map((item: { [prop: string]: any }) => {
      return {
        label: item?.text,
        value: item?.coverage,
        children: item.subItems.map((e: { [prop: string]: any }) => {
          return {
            label: e.text,
            value: e?.coverage,
          };
        }),
      };
    });

    const showCoverageColumn = benefitsFieldList.some((item: any) => {
      return item.value || item?.children?.some((e: any) => e.value);
    });

    const planInfoList = [
      {
        label: formatMessage({ id: 'Benefit' }),
        type: 'strong',
        value: showCoverageColumn ? formatMessage({ id: 'Coverage' }) : null,
      },
      ...benefitsFieldList,
      {
        label: formatMessage({ id: 'Add-ons Benefits' }),
        value: !includePa ? formatMessage({ id: '(None Selected)' }) : null,
        children: includePa
          ? planCmsInfo.soi_category === 'motorcycle'
            ? [
                {
                  label: formatMessage({
                    id: 'Personal Accident',
                  }),
                  value: formatMessage({
                    id: 'Up to VND 10.000.000',
                  }),
                },
              ]
            : [
                {
                  label: formatMessage({
                    id: 'Personal Accident (Passenger)',
                  }),
                  value: formatMessage({
                    id: 'Up to VND 150.000.000',
                  }),
                },
                {
                  label: formatMessage({
                    id: 'Personal Accident (Driver)',
                  }),
                  value: formatMessage({
                    id: 'Up to VND 10.000.000',
                  }),
                },
              ]
          : [],
        hidden:
          coverageType === 'cpd' ||
          ['travel', 'health', 'pa'].includes(productType),
      },
    ].filter((e) => !e.hidden);

    const coverage =
      productType === 'health' && quotationInfo?.fast_quote?.policy_duration
        ? formatMessage(
            {
              id: '{duration} Coverage',
            },
            {
              duration: (
                <FormattedMessage
                  {...specialIntlMessages[
                    quotationInfo?.fast_quote?.policy_duration
                  ]}
                />
              ),
            },
          )
        : undefined;

    return (
      <ListItem
        icon={logoUrl}
        title={planName}
        list={planInfoList}
        coverage={coverage}
      />
    );
  };

  const renderMotorPremiumDetailPanel = () => {
    const premiumInfo: { [prop: string]: any } = quotationInfo?.meta;
    const specificPremiumConfig = premiumConfig[productType][coverageType];

    const basicPremium = get(premiumInfo, specificPremiumConfig?.basicPremium);
    const paPremium = get(premiumInfo, specificPremiumConfig?.paPremium, 0);
    const vatPremium = get(
      premiumInfo,
      specificPremiumConfig?.vatPremium,
      null,
    );
    const grossPremium = numeral(basicPremium).add(paPremium).value() as number;
    const amountPayable = get(
      premiumInfo,
      specificPremiumConfig?.amountPayable,
      0,
    );

    const list = [
      {
        label: formatMessage({
          id: 'Basic Premium',
        }),
        value: <Amount amount={basicPremium} />,
      },
      {
        label: formatMessage({
          id: 'Add-ons Sub-total',
        }),
        value: <Amount amount={paPremium} />,
        children:
          paPremium > 0
            ? [
                {
                  label: formatMessage({ id: 'Personal Accident' }),
                  value: <Amount amount={paPremium} />,
                },
              ]
            : [],
        hidden:
          coverageType === 'cpd' ||
          ['travel', 'health', 'pa'].includes(productType),
      },
      {
        label: formatMessage({
          id: 'Total Gross Premium',
        }),
        value: <Amount amount={grossPremium} />,
        type: 'strong',
      },
      {
        label: formatMessage({
          id: 'VAT(10%)',
        }),
        value: <Amount amount={vatPremium} />,
        type: 'smallSpace',
        hidden: vatPremium === null,
      },
      {
        label: formatMessage({
          id: 'Amount Payable',
        }),
        value: <Amount amount={amountPayable} />,
        type: 'total',
        awalysShow: true,
      },
    ].filter((e) => !e.hidden) as any[];

    return (
      <ListItemPanel
        className={styles.listItemPanel}
        title={formatMessage({
          id: 'Premium Details',
        })}
        subTitle={formatMessage({
          id: 'All premiums in Vietnamese Dong',
        })}
        list={list}
        collapseDisabled
      />
    );
  };

  if (!quotationInfo?.fast_quote)
    return (
      <PageFrame title="">
        <></>
      </PageFrame>
    );

  return (
    <>
      <PageFrame
        title={formatMessage({
          id: 'Payment',
        })}
      >
        <div
          className={classnames([
            styles.mainPanel,
            { [styles['is-mobile']]: isMobile },
          ])}
        >
          {renderMotorPlan()}
          {renderMotorPremiumDetailPanel()}
          <Button
            className={classnames(['ignite-btn', styles.footerButton])}
            type="primary"
            onClick={() => {
              if (sessionStorage.getItem('userStatus') === '2') {
                iglooMessage.error(
                  formatMessage({
                    id: 'Your account has been deactivated, please contact your upline manager who you worked.',
                  }),
                );
                return;
              }
              handleMakePayment();
            }}
          >
            {formatMessage({
              id: 'Make Payment',
            })}
          </Button>
        </div>
      </PageFrame>
      <CopyToClipboard
        onCopy={() => {
          message.info({
            className: 'ignite-message-notice',
            content: formatMessage({
              id: 'Quote Link Copied.',
            }),
          });
        }}
        text={redirectToUrl}
      >
        <Button ref={copyButton} style={{ display: 'none' }} />
      </CopyToClipboard>
      <Modal
        visible={visible}
        width={600}
        title={formatMessage({
          id: 'Payment Option',
        })}
        onCancel={() => setVisible(false)}
        footer={
          <Row
            justify="end"
            gutter={isMobile ? [0, 12] : 16}
            style={{ width: '100%' }}
          >
            <Col {...(isMobile ? { span: 24 } : {})} order={isMobile ? 1 : 0}>
              <Button
                style={{ width: '100%' }}
                onClick={async () => {
                  const url = await payNow(true);
                  setRedirectToUrl(url as any as string);
                  setVisible(false);
                  url &&
                    setTimeout(() => {
                      copyButton.current?.click();
                    });
                  //event tracking: payment method
                  window.gtag('event', 'payment_method', {
                    method: 'own_payment',
                    user_unique_id: JSON.parse(
                      localStorage.getItem('user_info') ?? '{}',
                    )?.id,
                  });
                }}
              >
                {formatMessage({
                  id: 'Customer’s own Payment',
                })}
              </Button>
            </Col>
            <Col {...(isMobile ? { span: 24 } : {})} order={isMobile ? 0 : 1}>
              <Button
                type="primary"
                style={{ width: '100%' }}
                onClick={async () => {
                  const url = await payNow();
                  setVisible(false);
                  if (url) {
                    window.location.href = url as any as string;
                  }

                  //event tracking: payment method
                  window.gtag('event', 'payment_method', {
                    method: 'this device',
                    user_unique_id: JSON.parse(
                      localStorage.getItem('user_info') ?? '{}',
                    )?.id,
                  });
                }}
              >
                {formatMessage({
                  id: 'Pay on this Device',
                })}
              </Button>
            </Col>
          </Row>
        }
      >
        {formatMessage({
          id: 'Select your preferred payment method.',
        })}
      </Modal>
    </>
  );
};

export default Payment;
